import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/authContext';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { format } from 'date-fns';

const calculateDaysUntilReminder = (lastContactDate, reminderFrequency) => {
  if (!lastContactDate) return null;
  const now = new Date();
  const lastContact = new Date(lastContactDate);
  const nextReminderDate = new Date(lastContact.getTime() + (reminderFrequency * 24 * 60 * 60 * 1000));
  while (nextReminderDate < now) {
    nextReminderDate.setDate(nextReminderDate.getDate() + reminderFrequency);
  }
  const daysUntilReminder = Math.ceil((nextReminderDate - now) / (1000 * 60 * 60 * 24));
  return daysUntilReminder;
};

const getReminderStatus = (daysUntil, reminderStatus, hasActiveReminder = false) => {
  if (reminderStatus === 'wait' || reminderStatus === 'snoozed') {
    return {
      text: 'Connect tomorrow',
      className: 'bg-yellow-100 text-yellow-800'
    };
  }
  // Only show "Overdue" if there's an active reminder that's overdue
  // If no active reminder exists, we should have already set daysUntil to 0
  if (daysUntil < 0 && hasActiveReminder) {
    return {
      text: 'Overdue',
      className: 'bg-red-100 text-red-800'
    };
  }
  if (daysUntil === 0) {
    return {
      text: 'Connect today',
      className: 'bg-green-100 text-green-800' // Changed from orange to green
    };
  }
  if (daysUntil === 1) {
    return {
      text: 'Connect tomorrow',
      className: 'bg-gray-100 text-gray-800' // Changed from green to light gray
    };
  }
  if (daysUntil === 2) {
    return {
      text: 'Connect in 2 days',
      className: 'bg-gray-100 text-gray-800'
    };
  }
  return {
    text: `Connect in ${daysUntil} days`,
    className: 'bg-gray-100 text-gray-800'
  };
};

const SkeletonDiscipleCard = () => (
  <div className="block relative mb-4 shadow-sm bg-white p-4 rounded-lg">
    <div className="flex items-start space-x-4">
      <div className="flex-shrink-0 w-24 h-24">
        <Skeleton circle height={96} width={96} />
      </div>
      <div className="flex-1 min-w-0">
        <Skeleton height={24} width={120} style={{ marginBottom: 8 }} />
        <Skeleton height={16} width={80} style={{ marginBottom: 8 }} />
        <Skeleton count={2} height={12} width={180} style={{ marginBottom: 4 }} />
      </div>
    </div>
  </div>
);

// Helper to get initials from a name
function getInitials(name) {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const Dashboard = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const [userData, setUserData] = useState(null);
  const [userAppID, setUserAppID] = useState('');
  const [progressiveDisciples, setProgressiveDisciples] = useState([]);
  const [totalDisciples, setTotalDisciples] = useState(0);
  const [isLoadingDisciples, setIsLoadingDisciples] = useState(true);
  const [viewArchived, setViewArchived] = useState(false);
  const navigate = useNavigate();

  // Fetch user data
  const fetchUserData = async () => {
    if (currentUser && currentUser.uid) {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setUserData({ id: userDoc.id, ...userDoc.data() });
          setUserAppID(userDoc.data().appID);
        } else {
          setUserData(null);
        }
      } catch (error) {
        setUserData(null);
      }
    } else {
      setUserData(null);
    }
  };

  // Progressive fetch for disciples
  const fetchDisciples = async () => {
    setIsLoadingDisciples(true);
    setProgressiveDisciples([]);
    try {
      if (userData && userData.discipleIds && userData.discipleIds.length > 0) {
        setTotalDisciples(userData.discipleIds.length);
        for (const discipleId of userData.discipleIds) {
          const discipleRef = doc(db, 'disciples', discipleId);
          const discipleSnap = await getDoc(discipleRef);
          if (discipleSnap.exists()) {
            const discipleData = discipleSnap.data();
            let mentorDetails = {};
            if (userAppID) {
              const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);
              const mentorDetailsSnap = await getDoc(mentorDetailsRef);
              if (mentorDetailsSnap.exists()) {
                mentorDetails = mentorDetailsSnap.data();
                
                // Initialize all variables fresh for each disciple (prevent cross-contamination)
                const reminderFrequency = mentorDetails.reminderSettings?.frequency || 7;
                const lastMessageDate = mentorDetails.reminderSettings?.lastMessageDate || null;
                const nextReminderDate = mentorDetails.reminderSettings?.nextReminderDate || null;
                const lastReminderSent = mentorDetails.reminderSettings?.lastReminderSent || null;
                
                // Read nextNotificationDateAndTime (Priority 3 - preferred over nextReminderDate)
                let nextNotificationDateAndTime = null;
                try {
                  const settingsAny = mentorDetails.reminderSettings || {};
                  if (settingsAny && typeof settingsAny === 'object' && settingsAny !== null) {
                    const notificationDateAndTime = settingsAny['nextNotificationDateAndTime'];
                    if (notificationDateAndTime !== undefined && notificationDateAndTime !== null) {
                      // Handle Firestore Timestamp if needed
                      if (typeof notificationDateAndTime === 'object' && notificationDateAndTime !== null && 'toDate' in notificationDateAndTime) {
                        nextNotificationDateAndTime = notificationDateAndTime.toDate().toISOString();
                      } else if (typeof notificationDateAndTime === 'string') {
                        nextNotificationDateAndTime = notificationDateAndTime;
                      } else {
                        nextNotificationDateAndTime = String(notificationDateAndTime);
                      }
                    }
                  }
                } catch (error) {
                  // Property doesn't exist or can't be accessed - that's okay
                }
                
                // Initialize all calculation variables fresh for this disciple
                let daysUntil = null;
                let nextDate = null;
                let calculationMethod = 'none';
                let hasActiveReminder = false;
                let hasSnoozedReminder = false;
                let reminderStatusValue = null;
                let nextNotificationFormatted = null;
                let snoozedReminderData = null;
                
                // Helper function to safely parse dates
                const safeParseDate = (dateString) => {
                  if (!dateString) return null;
                  try {
                    // Handle Firestore timestamp
                    if (dateString && typeof dateString === 'object' && dateString.toDate) {
                      const date = dateString.toDate();
                      if (date && !isNaN(date.getTime())) {
                        return date;
                      }
                      return null;
                    }
                    // Handle string dates
                    if (typeof dateString === 'string') {
                      const date = new Date(dateString);
                      return isNaN(date.getTime()) ? null : date;
                    }
                    return null;
                  } catch (error) {
                    return null;
                  }
                };
                
                // Helper function to calculate days between dates
                // Normalize both dates to midnight for accurate day comparison
                const calculateDaysBetween = (futureDate, currentDate = new Date()) => {
                  // Normalize future date to midnight
                  const futureNormalized = new Date(futureDate.getFullYear(), futureDate.getMonth(), futureDate.getDate());
                  // Normalize current date to midnight
                  const currentNormalized = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
                  // Calculate difference in days
                  return Math.ceil((futureNormalized - currentNormalized) / (1000 * 60 * 60 * 24));
                };
                
                // PRIORITY 1: Check for active reminders in reminderQueue (highest priority)
                try {
                  const reminderQueueRef = collection(db, 'users', currentUser.uid, 'reminderQueue');
                  // Check BOTH archived and non-archived reminders
                  // (snoozed reminders might be archived but still have a future snoozedUntil)
                  const reminderQueueQuery = query(
                    reminderQueueRef,
                    where('discipleId', '==', discipleId)
                  );
                  const reminderQueueSnapshot = await getDocs(reminderQueueQuery);
                  
                  const now = new Date();
                  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  
                  for (const reminderDoc of reminderQueueSnapshot.docs) {
                    const reminderData = reminderDoc.data();
                    const reminderStatus = reminderData.reminderStatus;
                    const scheduledFor = reminderData.scheduledFor;
                    const snoozedUntil = reminderData.snoozedUntil;
                    const snoozeCount = reminderData.snoozeCount || 0;
                    const completedAt = reminderData.completedAt;
                    
                    // Check for snoozed reminders first (Priority 2 - they take priority for display)
                    // IMPORTANT: Check if snoozedUntil exists AND is in the future
                    // Even if reminder is archived, if it has a future snoozedUntil, it's still snoozed
                    if (snoozedUntil && !completedAt) {
                      const snoozedUntilDate = safeParseDate(snoozedUntil);
                      if (snoozedUntilDate) {
                        // Check if snooze is still active (snoozedUntil is in the future)
                        if (snoozedUntilDate > now) {
                          hasSnoozedReminder = true;
                          reminderStatusValue = 'snoozed';
                          if (!snoozedReminderData) {
                            snoozedReminderData = {
                              snoozedUntil: snoozedUntilDate,
                              snoozeCount: snoozeCount
                            };
                          }
                          // Calculate days until snooze expires (matching mobile logic exactly)
                          const daysUntilSnoozeExpires = calculateDaysBetween(snoozedUntilDate, now);
                          if (daysUntilSnoozeExpires <= 0) {
                            // Snooze expired, show today
                            daysUntil = 0;
                            nextDate = snoozedUntilDate;
                            calculationMethod = 'snoozed_expired';
                          } else {
                            // Snooze still active, show when it expires
                            daysUntil = daysUntilSnoozeExpires;
                            nextDate = snoozedUntilDate;
                            calculationMethod = 'snoozed_active';
                          }
                          console.log(`[Dashboard] ✅ Found snoozed reminder (archived=${reminderData.archived || false}) for ${discipleId}: snoozedUntil=${snoozedUntilDate.toISOString()}, daysUntil=${daysUntil}`);
                          // Continue checking for active reminders too (snoozed reminders can still be due)
                        }
                      }
                    }
                    
                    // Check for active reminders (sent or scheduled) that are due
                    const isActiveStatus = reminderStatus === 'sent' || reminderStatus === 'scheduled' || reminderStatus === 'queued';
                    const isNotCompleted = !completedAt;
                    
                    if (isActiveStatus && isNotCompleted && scheduledFor) {
                      const scheduledDate = safeParseDate(scheduledFor);
                      if (scheduledDate) {
                        const scheduledDay = new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate());
                        // If scheduled for today or in the past, it needs to be sent
                        if (scheduledDay.getTime() <= today.getTime()) {
                          hasActiveReminder = true;
                          nextDate = scheduledDate;
                          // Calculate days difference - allow negative for overdue (only for active reminders)
                          const daysDiff = Math.ceil((scheduledDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                          daysUntil = daysDiff < 0 ? daysDiff : 0;
                          calculationMethod = 'active_reminder';
                          reminderStatusValue = reminderStatus;
                          break; // Found an active reminder, no need to check others
                        }
                      }
                    }
                  }
                } catch (reminderQueueError) {
                  // If reminderQueue query fails, continue with normal logic
                  console.log('Could not check reminderQueue for disciple:', discipleId, reminderQueueError);
                }
                
                // PRIORITY 3: Use nextNotificationDateAndTime if available (preferred - has time component)
                if (!hasActiveReminder && !hasSnoozedReminder && nextNotificationDateAndTime) {
                  const parsedNotificationDate = safeParseDate(nextNotificationDateAndTime);
                  if (parsedNotificationDate) {
                    nextDate = parsedNotificationDate;
                    daysUntil = calculateDaysBetween(parsedNotificationDate);
                    calculationMethod = 'nextNotificationDateAndTime';
                    // Format next notification for display
                    try {
                      const formatted = format(parsedNotificationDate, 'MMM d, h:mm').toLowerCase();
                      const ampm = format(parsedNotificationDate, 'a').toLowerCase();
                      nextNotificationFormatted = `${formatted}${ampm === 'am' ? 'a' : 'p'}`;
                    } catch (error) {
                      // Formatting failed, skip microcopy
                    }
                  }
                }
                
                // PRIORITY 4: Fallback to nextReminderDate if nextNotificationDateAndTime not available
                else if (!hasActiveReminder && !hasSnoozedReminder && nextReminderDate) {
                  const parsedNextDate = safeParseDate(nextReminderDate);
                  if (parsedNextDate) {
                    nextDate = parsedNextDate;
                    daysUntil = calculateDaysBetween(parsedNextDate);
                    calculationMethod = 'nextReminderDate';
                  } else {
                    console.warn('Invalid nextReminderDate:', nextReminderDate, 'for disciple:', discipleData.name);
                  }
                }
                
                // PRIORITY 5: Calculate from lastMessageDate + frequency
                else if (!hasActiveReminder && !hasSnoozedReminder && lastMessageDate) {
                  const parsedLastDate = safeParseDate(lastMessageDate);
                  if (parsedLastDate) {
                    // Calculate next reminder based on last message + frequency
                    const calculatedNextDate = new Date(parsedLastDate);
                    calculatedNextDate.setDate(calculatedNextDate.getDate() + reminderFrequency);
                    
                    // If the calculated date is in the past, keep adding frequency until we get a future date
                    const now = new Date();
                    while (calculatedNextDate <= now) {
                      calculatedNextDate.setDate(calculatedNextDate.getDate() + reminderFrequency);
                    }
                    
                    nextDate = calculatedNextDate;
                    daysUntil = calculateDaysBetween(calculatedNextDate);
                    calculationMethod = 'lastMessageDate_calculation';
                  } else {
                    console.warn('Invalid lastMessageDate:', lastMessageDate, 'for disciple:', discipleData.name);
                  }
                }
                
                // PRIORITY 6: Brand new disciple - no message history (lowest priority)
                else if (!hasActiveReminder && !hasSnoozedReminder) {
                  // For brand new disciples, show today (0 days)
                  daysUntil = 0;
                  calculationMethod = 'new_disciple';
                  nextDate = new Date(); // Today
                }
                
                // Final validation - ensure daysUntil is always a valid number
                if (daysUntil === null || isNaN(daysUntil) || !isFinite(daysUntil)) {
                  console.warn('Invalid daysUntil calculated:', daysUntil, 'for disciple:', discipleData.name, 'method:', calculationMethod);
                  // Ultimate fallback: show today
                  daysUntil = 0;
                  calculationMethod = 'fallback';
                  nextDate = new Date();
                }
                
                // Only allow negative days (overdue) if there's an active reminder
                // If no active reminder exists, use the next reminder date (which should be in the future)
                // This prevents showing "Overdue" for retired/archived reminders
                if (daysUntil < 0 && !hasActiveReminder) {
                  // No active reminder, but date is in past - this means next reminder cycle is due
                  // Show as "Connect today" instead of "Overdue"
                  daysUntil = 0;
                }
                
                // Store snoozed reminder data for display
                const snoozedReminderInfo = hasSnoozedReminder && snoozedReminderData ? {
                  snoozedUntil: snoozedReminderData.snoozedUntil,
                  snoozeCount: snoozedReminderData.snoozeCount
                } : null;
                
                mentorDetails = {
                  ...mentorDetails,
                  reminderMetrics: {
                    frequency: reminderFrequency,
                    daysUntilReminder: daysUntil,
                    nextReminderDate: nextDate,
                    nextNotificationDateAndTime: nextNotificationDateAndTime,
                    nextNotificationFormatted: nextNotificationFormatted,
                    calculationMethod: calculationMethod,
                    enabled: mentorDetails.reminderSettings?.enabled || false,
                    hasActiveReminder: hasActiveReminder,
                    hasSnoozedReminder: hasSnoozedReminder,
                    reminderStatus: reminderStatusValue,
                    snoozedReminderInfo: snoozedReminderInfo
                  },
                  archived: mentorDetails.archived || false,
                };
              }
            }
            const disciple = { id: discipleId, ...discipleData, mentorDetails };
            // Only add to the current view (active or archived)
            if (!!mentorDetails.archived === !!viewArchived) {
              setProgressiveDisciples(prev => [...prev, disciple]);
            }
          }
        }
      } else {
        setProgressiveDisciples([]);
        setTotalDisciples(0);
      }
    } catch (error) {
      setProgressiveDisciples([]);
      setTotalDisciples(0);
    } finally {
      setIsLoadingDisciples(false);
    }
  };

  useEffect(() => {
    if (currentUser && currentUser.uid) {
      fetchUserData();
    }
  }, [currentUser]);

  useEffect(() => {
    if (userAppID) {
      fetchDisciples();
    }
  }, [userAppID, viewArchived]);

  // Sort disciples by urgency (daysUntilReminder) first, then alphabetically by name
  const sortedDisciples = React.useMemo(() => {
    return [...progressiveDisciples].sort((a, b) => {
      // Get daysUntilReminder for both disciples
      const aDaysUntil = a.mentorDetails?.reminderMetrics?.daysUntilReminder ?? 999;
      const bDaysUntil = b.mentorDetails?.reminderMetrics?.daysUntilReminder ?? 999;
      
      // First sort by urgency (lower daysUntil = more urgent)
      if (aDaysUntil !== bDaysUntil) {
        return aDaysUntil - bDaysUntil;
      }
      
      // If urgency is the same, sort alphabetically by name
      const aName = (a.name || '').toLowerCase();
      const bName = (b.name || '').toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [progressiveDisciples]);

  const dashboardBgStyle = {
    minHeight: '100vh',
    backgroundImage: 'url(/dashboard-bg.png)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundAttachment: 'fixed',
  };

  if (authLoading) {
    // Always show skeletons while loading auth
    return (
      <div className="min-h-screen flex items-center justify-center relative" style={dashboardBgStyle}>
        <div className="absolute inset-0 bg-gray-900/50" aria-hidden />
        <div className="relative z-10">{[...Array(3)].map((_, i) => <SkeletonDiscipleCard key={i} />)}</div>
      </div>
    );
  }
  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="min-h-screen w-full relative" style={dashboardBgStyle}>
      <div className="absolute inset-0 bg-gray-900/50" aria-hidden />
      <div className="relative z-10 max-w-7xl mx-auto pt-10 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl shadow-xl p-8 md:p-10 relative bg-white/85 backdrop-blur-md">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <div>
              <h2 className="text-2xl font-extrabold text-gray-900 mb-1 tracking-tight">My Disciples</h2>
              <p className="text-gray-500 text-sm font-medium">A lifetime of impact one message at a time.</p>
            </div>
            {/* Desktop Add Disciple Button */}
            <Link
              to="/add-disciple"
              className="hidden sm:block bg-red-500 text-white px-6 py-2 rounded-full shadow hover:bg-red-600 transition text-lg font-semibold"
            >
              Add Disciple
            </Link>
            {/* Mobile Add Disciple Button */}
            <Link
              to="/add-disciple"
              className="sm:hidden absolute top-6 right-6 w-10 h-10 bg-red-500 text-white rounded-full shadow hover:bg-red-600 transition flex items-center justify-center text-xl font-bold"
            >
              +
            </Link>
          </div>
          {/* Disciples list area: show skeletons or data */}
          {(!userData || isLoadingDisciples) ? (
            <div>
              {[...Array(Math.max(totalDisciples, 3))].map((_, i) => <SkeletonDiscipleCard key={i} />)}
            </div>
                      ) : progressiveDisciples.length === 0 ? (
              <div className="text-center py-16 px-4">
                {viewArchived ? (
                  <div className="text-gray-400">No archived disciples.</div>
                ) : (
                  <>
                    {/* Illustration */}
                    <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#0076BE]/10 to-[#5ABA47]/10 flex items-center justify-center mb-6">
                      <svg className="w-10 h-10 text-[#0076BE]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-2">Your First Step Starts Here</h3>
                    <p className="text-gray-600 max-w-md mx-auto mb-2">
                      Think of one person God has placed on your heart — a friend, a coworker, a neighbor. 
                      Add them as your first disciple and let TOM help you stay faithful to that relationship.
                    </p>
                    <p className="text-gray-400 text-sm max-w-sm mx-auto mb-8">
                      You don't need to have all the answers. You just need to show up. We'll remind you when it's time to reach out and give you something meaningful to share.
                    </p>

                    <Link
                      to="/add-disciple"
                      className="inline-flex items-center gap-2 bg-[#5ABA47] text-white px-8 py-3 rounded-full shadow-lg hover:bg-[#4a9939] transition text-lg font-semibold"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Your First Disciple
                    </Link>

                    <p className="mt-6 text-xs text-gray-400 italic">"Go therefore and make disciples of all nations" — Matthew 28:19</p>
                  </>
                )}
              </div>
            ) : (
              <>
            <div>
              {/* Mobile View (< 768px) */}
              <div className="md:hidden -mx-[24px] -mb-6">
                {sortedDisciples.map((disciple) => (
                  <Link
                    to={`/disciple-menu/${disciple.id}`}
                    key={disciple.id}
                    className="block bg-gray-50 mb-6 rounded-2xl shadow-md hover:shadow-lg transition border border-gray-200 overflow-hidden"
                  >
                    <div className="flex h-28">
                      <div className="h-28 w-28 relative flex-shrink-0">
                        {disciple.mentorDetails?.photoURL ? (
                          <img
                            src={disciple.mentorDetails.photoURL}
                            alt=""
                            className="h-full w-full object-cover rounded-l-2xl"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-l-2xl">
                            <span className="text-2xl font-bold text-gray-500">{getInitials(disciple.name)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 p-5 flex flex-col justify-center min-w-0">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2.5">
                          {disciple.name || 'No Name Provided'}
                        </h2>
                        <div className="flex flex-col space-y-2.5">
                          {disciple.mentorDetails?.reminderMetrics?.enabled && 
                           disciple.mentorDetails?.reminderMetrics?.daysUntilReminder != null && (
                            <>
                              {/* Special handling for snoozed reminders */}
                              {disciple.mentorDetails.reminderMetrics.hasSnoozedReminder && 
                               disciple.mentorDetails.reminderMetrics.snoozedReminderInfo && (
                                <span className="inline-flex self-start items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  Snoozed till {new Date(disciple.mentorDetails.reminderMetrics.snoozedReminderInfo.snoozedUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              )}
                              {/* Regular reminder status */}
                              {!disciple.mentorDetails.reminderMetrics.hasSnoozedReminder && (
                                <span className={`inline-flex self-start items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  getReminderStatus(
                                    disciple.mentorDetails.reminderMetrics.daysUntilReminder,
                                    disciple.mentorDetails.reminderMetrics.reminderStatus,
                                    disciple.mentorDetails.reminderMetrics.hasActiveReminder
                                  ).className
                                }`}>
                                  {getReminderStatus(
                                    disciple.mentorDetails.reminderMetrics.daysUntilReminder,
                                    disciple.mentorDetails.reminderMetrics.reminderStatus,
                                    disciple.mentorDetails.reminderMetrics.hasActiveReminder
                                  ).text}
                                </span>
                              )}
                              {disciple.mentorDetails.reminderMetrics.nextNotificationFormatted && (
                                <span className="text-xs text-gray-400">
                                  Next notification: {disciple.mentorDetails.reminderMetrics.nextNotificationFormatted}
                                </span>
                              )}
                            </>
                          )}
                          {disciple.mentorDetails?.lastNote?.note && (
                            <span className="text-sm text-gray-500 truncate pr-4">
                              {disciple.mentorDetails.lastNote.note}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              
              {/* Tablet & Desktop View (≥ 768px) */}
              <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {sortedDisciples.map((disciple) => (
                  <Link
                    to={`/disciple-menu/${disciple.id}`}
                    key={disciple.id}
                    className="block text-center bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="w-28 h-28 mx-auto relative">
                      {disciple.mentorDetails?.photoURL ? (
                        <img
                          src={disciple.mentorDetails.photoURL}
                          alt=""
                          className="h-full w-full object-cover rounded-full"
                        />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-full">
                          <span className="text-3xl font-bold text-gray-500">{getInitials(disciple.name)}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-4">
                      <h2 className="text-xl font-semibold text-gray-900 truncate">
                        {disciple.name || 'No Name Provided'}
                      </h2>
                      {disciple.mentorDetails?.reminderMetrics?.enabled && 
                       disciple.mentorDetails?.reminderMetrics?.daysUntilReminder != null && (
                        <div className="mt-3">
                          {/* Special handling for snoozed reminders */}
                          {disciple.mentorDetails.reminderMetrics.hasSnoozedReminder && 
                           disciple.mentorDetails.reminderMetrics.snoozedReminderInfo && (
                            <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                              Snoozed till {new Date(disciple.mentorDetails.reminderMetrics.snoozedReminderInfo.snoozedUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                          {/* Regular reminder status */}
                          {!disciple.mentorDetails.reminderMetrics.hasSnoozedReminder && (
                            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                              getReminderStatus(
                                disciple.mentorDetails.reminderMetrics.daysUntilReminder,
                                disciple.mentorDetails.reminderMetrics.reminderStatus,
                                disciple.mentorDetails.reminderMetrics.hasActiveReminder
                              ).className
                            }`}>
                              {getReminderStatus(
                                disciple.mentorDetails.reminderMetrics.daysUntilReminder,
                                disciple.mentorDetails.reminderMetrics.reminderStatus,
                                disciple.mentorDetails.reminderMetrics.hasActiveReminder
                              ).text}
                            </span>
                          )}
                          {disciple.mentorDetails.reminderMetrics.nextNotificationFormatted && (
                            <div className="mt-1 text-xs text-gray-400">
                              Next notification: {disciple.mentorDetails.reminderMetrics.nextNotificationFormatted}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Archived Disciples Button at Bottom */}
            <div className="flex justify-center mt-8">
              <button
                onClick={() => setViewArchived((prev) => !prev)}
                className="text-sm text-gray-500 hover:text-blue-600 transition border px-3 py-1 rounded"
              >
                {viewArchived ? 'Show Active Disciples' : 'Show Archived Disciples'}
              </button>
            </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;